import { Component, forwardRef, Input, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, map, startWith } from 'rxjs';

@Component({
  selector: 'app-chips',
  templateUrl: './chips.component.html',
  styleUrls: ['./chips.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipsComponent),
      multi: true,
    },
  ],
})

export class ChipsComponent implements ControlValueAccessor {
  @Input() options: string[] = [];
  @Output() valueChange = new EventEmitter<string[]>();

  values: string[] = [];
  inputCtrl = new FormControl('');
  filteredOptions: Observable<string[]>;

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.filteredOptions = this.inputCtrl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value))
    );
  }

  writeValue(value: string[] | null): void {
    this.values = value || [];
  }
  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  markAsTouched() {
    this.onTouched();
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputCtrl.disable();
    } else {
      this.inputCtrl.enable();
    }
  }

  addFromInput(event: KeyboardEvent): void {
    const input = (event.target as HTMLInputElement).value?.trim();
    if (event.key === 'Enter' && input) {
      if (!this.values.includes(input)) {
        this.values.push(input);
        this.onChange(this.values);
        this.valueChange.emit(this.values);
      }
      this.inputCtrl.setValue('');
      event.preventDefault(); // prevent form submit
    }
  }

  remove(value: string): void {
    const index = this.values.indexOf(value);
    if (index >= 0) {
      this.values.splice(index, 1);
      this.onChange(this.values);
      this.valueChange.emit(this.values);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.viewValue;
    if (!this.values.includes(value)) {
      this.values.push(value);
      this.onChange(this.values);
      this.valueChange.emit(this.values);
    }
    this.inputCtrl.setValue('');
  }

  private _filter(value: string | null): string[] {
    const filterValue = (value || '').toLowerCase();
    return this.options.filter((option) =>
      option.toLowerCase().includes(filterValue)
    );
  }
}
